import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, StatusBadge, TEACHER_NAV } from "@/components/EduShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { eduRpc, formatDue } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/assignments/")({
  component: TeacherAssignments,
  head: () => ({
    meta: [
      { title: "Assignments - Inquira teacher" },
      { name: "description", content: "Create, publish, duplicate and review every assignment across your Inquira classes." },
      { property: "og:title", content: "Inquira assignments" },
      { property: "og:description", content: "One place for drafts, published work and grading." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Row {
  id: string;
  title: string;
  kind: string | null;
  status: string | null;
  due_at: string | null;
  class_id: string;
  classes: { name: string } | null;
}

const FILTERS = ["all", "draft", "published", "closed"] as const;

function TeacherAssignments() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("assignments")
      .select("id, title, kind, status, due_at, class_id, classes(name)")
      .eq("teacher_id", user.id)
      .order("created_at", { ascending: false });
    setRows((data as unknown as Row[]) ?? []);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  async function duplicate(id: string) {
    const { error } = await eduRpc<string>("edu_duplicate_assignment", { _assignment_id: id });
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.teacher_assignments_index.copied_as_a_new_draft"));
    void load();
  }

  const visible = rows.filter((r) => filter === "all" || (r.status ?? "draft") === filter);

  return (
    <EduShell
      nav={TEACHER_NAV}
      title={i18n.t("edu.teacher_assignments_index.assignments")}
      subtitle={i18n.t("edu.teacher_assignments_index.drafts_stay_private_until_you_publish_th")}
      actions={
        <Link to="/teacher/assignments/new" search={{ classId: undefined }} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          New assignment
        </Link>
      }
    >
      <div className="mb-5 flex gap-1 text-sm">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1.5 font-semibold capitalize ${
              filter === f ? "border border-rule bg-card text-ink" : "text-ink-muted"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState title={i18n.t("edu.teacher_assignments_index.nothing_here_yet")} body="Create an assignment, or switch the filter above." />
      ) : (
        <ul className="space-y-2">
          {visible.map((r) => (
            <li key={r.id} className="rounded-2xl border border-rule bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link to="/teacher/assignments/$assignmentId" params={{ assignmentId: r.id }} className="font-semibold text-ink">
                  {r.title}
                </Link>
                <div className="flex items-center gap-3 text-xs text-ink-muted">
                  <StatusBadge status={r.status ?? "draft"} />
                  <span>{formatDue(r.due_at)}</span>
                  <button className="font-semibold hover:text-ink" onClick={() => duplicate(r.id)}><T k="edu.teacher_assignments_index.duplicate" /></button>
                </div>
              </div>
              <p className="mt-1 text-sm text-ink-muted">
                {[r.classes?.name, r.kind].filter(Boolean).join(" · ")}
              </p>
            </li>
          ))}
        </ul>
      )}
    </EduShell>
  );
}
